import _ from 'lodash';
import React, { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import Select from 'react-select';
import Button from 'src/components/controls/forms/redesign/FormButton';
import RHFInputBox from 'src/components/controls/tempRHFControls/RHFInputBox';
import Fieldset from 'src/components/project-billing/components/FieldSetComponent';
import Panel from 'src/components/common/Panel';
import PagedResultSet from 'src/models/common/PagedResultSet';
import Vendor from 'src/models/AP/Vendor';

export interface IVoucherSearchCriteria {
    vendorNumber?: string;
    voucherNumber?: string;
    vendorInvoice?: string;
    invoiceDate?: string;
    invoiceAmount?: string;
    voucherAmount?: string;
    description?: string;
    dueDate?: string;
    closeDate?: string;
    checkNumber?: string;
    checkDate?: string;
    checkVoid?: string;
    checkVoidDate?: string;
    voucherVoid?: string;
    voucherVoidDate?: string;
    reoccurNumber?: string;
    paymentNumber?: string;
}

interface IVoucherSearchProps {
    vendors?: PagedResultSet<Vendor>;
    onVendorSearchChange: (term: string) => void;
    onSearch: (criteria: IVoucherSearchCriteria) => void;
}

interface IOptionType {
    value: string;
    label: string;
}

// Port of ap_voucher.fmt's native Enter-Query behavior on the AP_VOUCHER block - every field the
// Oracle form's own header block exposes (see ap_voucher.fmt.plsql.txt's :ap_voucher.* references)
// is a legitimate search criterion there, so this panel surfaces the same set rather than the
// single Find-Vendor-only path VouchersPage.tsx previously relied on for narrowing the voucher list.
const VoucherSearch = (props: IVoucherSearchProps) => {
    const { register, errors, getValues, control, reset, setValue } = useForm<IVoucherSearchCriteria>({
        defaultValues: { checkVoid: '', voucherVoid: '' }
    });
    const [isSearchingVendors, setIsSearchingVendors] = useState<boolean>(false);
    const [isCollapsed, setIsCollapsed] = useState<boolean>(false);

    const vendorOptions: Array<IOptionType> = (props.vendors?.items || []).map(v => ({
        value: `${v.vendorNumber}`,
        label: `${v.vendorName} (${v.vendorNumber})`
    }));

    const vendorSearch = _.debounce((inputText: string) => {
        if (!!inputText && inputText.length > 0) {
            props.onVendorSearchChange(inputText);
        }
    }, 500);

    const handleVendorSearchChanged = (inputText: string) => {
        vendorSearch(inputText);
    };

    const handleSearch = () => {
        const data = getValues();
        props.onSearch({
            vendorNumber: data.vendorNumber,
            voucherNumber: data.voucherNumber,
            vendorInvoice: data.vendorInvoice,
            invoiceDate: data.invoiceDate,
            invoiceAmount: data.invoiceAmount,
            voucherAmount: data.voucherAmount,
            description: data.description,
            dueDate: data.dueDate,
            closeDate: data.closeDate,
            checkNumber: data.checkNumber,
            checkDate: data.checkDate,
            checkVoid: data.checkVoid,
            checkVoidDate: data.checkVoidDate,
            voucherVoid: data.voucherVoid,
            voucherVoidDate: data.voucherVoidDate,
            reoccurNumber: data.reoccurNumber,
            paymentNumber: data.paymentNumber
        });
    };

    const handleClear = () => {
        reset();
        props.onSearch({});
    };

    return (
        <Panel backgroundColor="#D3D3D3">
            <Panel panelTitle="Find Vouchers">
                <div style={{ padding: '0 .5rem', marginTop: '-1rem' }}>
                    <div className="form-row">
                    {!isCollapsed && <>
                        <div style={{ width: '16%', marginRight: '.5rem' }}>
                            <label style={{ whiteSpace: 'nowrap' }}>Vendor No.</label>
                            <Controller
                                name="vendorNumber"
                                control={control}
                                defaultValue={null}
                                render={({ onChange, value }) => (
                                    <Select
                                        placeholder="Select Vendor"
                                        options={vendorOptions}
                                        value={value ? (vendorOptions.find(o => o.value === value) ?? null) : null}
                                        onMenuOpen={() => setIsSearchingVendors(true)}
                                        onMenuClose={() => setIsSearchingVendors(false)}
                                        onBlur={() => setIsSearchingVendors(false)}
                                        menuIsOpen={isSearchingVendors}
                                        isSearchable={true}
                                        isClearable={true}
                                        onInputChange={handleVendorSearchChanged}
                                        onChange={(opt: IOptionType | null) => onChange(opt?.value ?? null)}
                                    />
                                )}
                            />
                        </div>
                        {/* Sized to comfortably fit 2 orders of magnitude above the live max VCH_NO
                            (41415 -> 4,141,500, 7 digits) rather than the 12% share every other
                            field in this row gets. */}
                        <RHFInputBox
                            label="Voucher No."
                            labelStyle={{ whiteSpace: 'nowrap' }}
                            name="voucherNumber"
                            type="number"
                            register={register}
                            errors={errors}
                            bodyStyle={{ width: '14ch', marginRight: '.5rem' }}
                            style={{ width: '100%' }}
                        />
                        <RHFInputBox
                            label="Vendor Invoice"
                            labelStyle={{ whiteSpace: 'nowrap' }}
                            name="vendorInvoice"
                            type="text"
                            register={register}
                            errors={errors}
                            bodyStyle={{ width: '14%', marginRight: '.5rem' }}
                            style={{ width: '100%' }}
                        />
                        <RHFInputBox
                            label="Description"
                            labelStyle={{ whiteSpace: 'nowrap' }}
                            name="description"
                            type="text"
                            register={register}
                            errors={errors}
                            bodyStyle={{ width: '16%', marginRight: '.5rem' }}
                            style={{ width: '100%' }}
                        />
                        <RHFInputBox
                            label="Reoccur No."
                            labelStyle={{ whiteSpace: 'nowrap' }}
                            name="reoccurNumber"
                            type="number"
                            register={register}
                            errors={errors}
                            bodyStyle={{ width: '14ch', marginRight: '.5rem' }}
                            style={{ width: '100%' }}
                        />
                        <RHFInputBox
                            label="Invoice Date"
                            labelStyle={{ whiteSpace: 'nowrap' }}
                            name="invoiceDate"
                            type="date"
                            register={register}
                            errors={errors}
                            setValue={setValue}
                            clearable={true}
                            bodyStyle={{ width: '8%', marginRight: '.5rem' }}
                            style={{ width: '100%' }}
                        />
                        <RHFInputBox
                            label="Invoice Amount"
                            labelStyle={{ whiteSpace: 'nowrap' }}
                            name="invoiceAmount"
                            type="number"
                            register={register}
                            errors={errors}
                            bodyStyle={{ width: '14ch', marginRight: '.5rem' }}
                            style={{ width: '100%' }}
                        />
                    </>}
                        <Button
                            text={'Search'}
                            className="blue-hover-button"
                            style={{ width: '7rem', height: '3rem', verticalAlign: 'middle', marginLeft: '1rem', marginTop: '1.7rem', borderRadius: '5px' }}
                            onClick={() => handleSearch()}
                        />
                        <Button
                            text={isCollapsed ? '(+) Expand' : '(-) Minimize'}
                            className="blue-hover-button"
                            style={{ whiteSpace: 'nowrap', marginLeft: '1rem', marginTop: '1.7rem', width: '9rem', height: '3rem', verticalAlign: 'middle', borderRadius: '5px' }}
                            onClick={() => setIsCollapsed(!isCollapsed)}
                        />
                        <Button
                            text="Clear"
                            className="blue-hover-button"
                            style={{ whiteSpace: 'nowrap', marginLeft: '1rem', marginTop: '1.7rem', width: '7rem', height: '3rem', verticalAlign: 'middle', borderRadius: '5px' }}
                            onClick={() => handleClear()}
                        />
                    </div>
                    {!isCollapsed && <>
                    <div className="form-row" style={{ marginTop: '.5rem', alignItems: 'flex-end' }}>
                        {/* Sized to 1 order of magnitude beyond the live max AMOUNT (4,483,072.84 -> 44,830,728.40, 8 integer digits), widened to match the "Voucher Amount" label. */}
                        <RHFInputBox
                            label="Voucher Amount"
                            labelStyle={{ whiteSpace: 'nowrap' }}
                            name="voucherAmount"
                            type="number"
                            register={register}
                            errors={errors}
                            bodyStyle={{ width: '14ch', marginRight: '.5rem' }}
                            style={{ width: '100%' }}
                        />
                        <RHFInputBox
                            label="Due Date"
                            labelStyle={{ whiteSpace: 'nowrap' }}
                            name="dueDate"
                            type="date"
                            register={register}
                            errors={errors}
                            setValue={setValue}
                            clearable={true}
                            bodyStyle={{ width: '8%', marginRight: '.5rem' }}
                            style={{ width: '100%' }}
                        />
                        <RHFInputBox
                            label="Close Date"
                            labelStyle={{ whiteSpace: 'nowrap' }}
                            name="closeDate"
                            type="date"
                            register={register}
                            errors={errors}
                            setValue={setValue}
                            clearable={true}
                            bodyStyle={{ width: '8%', marginRight: '.5rem' }}
                            style={{ width: '100%' }}
                        />
                        <RHFInputBox
                            label="Payment No."
                            labelStyle={{ whiteSpace: 'nowrap' }}
                            name="paymentNumber"
                            type="number"
                            register={register}
                            errors={errors}
                            bodyStyle={{ width: '14ch', marginRight: '.5rem' }}
                            style={{ width: '100%' }}
                        />
                        <RHFInputBox
                            label="Check No."
                            labelStyle={{ whiteSpace: 'nowrap' }}
                            name="checkNumber"
                            type="number"
                            register={register}
                            errors={errors}
                            bodyStyle={{ width: '14ch', marginRight: '.5rem' }}
                            style={{ width: '100%' }}
                        />
                        <RHFInputBox
                            label="Check Date"
                            labelStyle={{ whiteSpace: 'nowrap' }}
                            name="checkDate"
                            type="date"
                            register={register}
                            errors={errors}
                            setValue={setValue}
                            clearable={true}
                            bodyStyle={{ width: '8%', marginRight: '.5rem' }}
                            style={{ width: '100%' }}
                        />
                        <Fieldset
                            label="Check Void?"
                            style={{
                                border: '1px solid black',
                                padding: '4px 6px 6px',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '12px',
                                borderRadius: '4px',
                                marginRight: '.5rem',
                                paddingLeft: '4px'
                            }}
                        >
                            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', margin: 0, whiteSpace: 'nowrap' }}>
                                <input type="radio" name="checkVoid" ref={register} value="" defaultValue="" defaultChecked={true} />
                                All
                            </label>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', margin: 0, whiteSpace: 'nowrap' }}>
                                <input type="radio" name="checkVoid" ref={register} value="Y" />
                                Yes
                            </label>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', margin: 0, whiteSpace: 'nowrap' }}>
                                <input type="radio" name="checkVoid" ref={register} value="N" />
                                No
                            </label>
                        </Fieldset>
                        <RHFInputBox
                            label="Date Voided"
                            labelStyle={{ whiteSpace: 'nowrap' }}
                            name="checkVoidDate"
                            type="date"
                            register={register}
                            errors={errors}
                            setValue={setValue}
                            clearable={true}
                            bodyStyle={{ width: '8%', marginRight: '.5rem' }}
                            style={{ width: '100%' }}
                        />
                        <Fieldset
                            label="Voucher Void?"
                            style={{
                                border: '1px solid black',
                                padding: '4px 6px 6px',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '12px',
                                borderRadius: '4px',
                                marginRight: '.5rem',
                                paddingLeft: '4px'
                            }}
                        >
                            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', margin: 0, whiteSpace: 'nowrap' }}>
                                <input type="radio" name="voucherVoid" ref={register} value="" defaultValue="" defaultChecked={true} />
                                All
                            </label>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', margin: 0, whiteSpace: 'nowrap' }}>
                                <input type="radio" name="voucherVoid" ref={register} value="Y" />
                                Yes
                            </label>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', margin: 0, whiteSpace: 'nowrap' }}>
                                <input type="radio" name="voucherVoid" ref={register} value="N" />
                                No
                            </label>
                        </Fieldset>
                        <RHFInputBox
                            label="Date Voided"
                            labelStyle={{ whiteSpace: 'nowrap' }}
                            name="voucherVoidDate"
                            type="date"
                            register={register}
                            errors={errors}
                            setValue={setValue}
                            clearable={true}
                            bodyStyle={{ width: '8%', marginRight: '1rem' }}
                            style={{ width: '100%' }}
                        />
                    </div>
                    </>}
                </div>
            </Panel>
        </Panel>
    );
};

export default VoucherSearch;



// WEBPACK FOOTER //
// ./src/components/accounts-payable/components/VoucherSearch.tsx